import React, { useEffect, useState } from 'react';
import './Event.scss';
import { getData } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import DateRender from '../components/DateRender';
import Button from '../components/Button';
import { SessionBackgroundData } from './Session';
import Background from '../components/Background';
import { ColourStyleEnum, FontStyleEnum } from '../models/api';
import { ReactComponent as ArrowIcon } from '../assets/arrow_forward_large.svg';

interface EventResponse {
  name: string;
  iconUrl: string | null;
  welcomeImageUrl: string | null;
  colourStyleEnum: ColourStyleEnum;
  fontStyleEnum: FontStyleEnum;
  showings: EventShowingResponse[];
  defaultShowing: EventShowingResponse;
  background: SessionBackgroundData | null;
}
interface EventShowingResponse {
  reference: string;
  location: string;
  startTimeUtc: string;
  isPresenting: string;
}

const Event: React.FC = () => {
  const [event, setEvent] = useState<EventResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [showing, setShowing] = useState('');

  const { reference } = useParams();
  const navigate = useNavigate();

  const getEventData = async () => {
    const result = await getData<EventResponse>(
      `api/sessions/references/${reference}`
    );
    if (result.success && result.data) {
      if (result.data.showings.length === 0 && result.data.defaultShowing) {
        navigate(`/s/${result.data.defaultShowing.reference}`);
        return;
      }
      if (result.data.showings.length === 1) {
        navigate(`/s/${result.data.showings[0].reference}`);
        return;
      }
      setEvent(result.data);
      if (result.data.showings?.some((p) => p.isPresenting)) {
        setShowing(
          result.data.showings.filter((p) => p.isPresenting)[0].reference
        );
      } else if (
        result.data.showings.some((p) => new Date(p.startTimeUtc) > new Date())
      ) {
        setShowing(
          result.data.showings.filter(
            (p) => new Date(p.startTimeUtc) > new Date()
          )[0].reference
        );
      } else if (result.data.showings?.length > 0) {
        setShowing(result.data.showings[0].reference);
      }
    } else {
      setErrors(result.errors);
    }
    setLoading(false);
  };

  const go = () => {
    if (showing) {
      navigate(`/s/${showing}`);
    }
  };

  useEffect(() => {
    getEventData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!event) {
    return <></>;
  }

  // TODO: Avoid duplication from Session.tsx
  const getStyleClass = () => {
    const g = event.colourStyleEnum
      ? `color-style-${event.colourStyleEnum}`
      : '';
    const t = event.fontStyleEnum ? ` font-style-${event.fontStyleEnum}` : '';

    return 'content-page ' + g + t;
  };

  const getShowingName = (showing: EventShowingResponse) => {
    if (!showing.location) {
      return <DateRender dateString={showing.startTimeUtc} />;
    }
    return (
      <>
        {showing.location} - <DateRender dateString={showing.startTimeUtc} />
      </>
    );
  };

  return (
    <div className={getStyleClass()}>
      <Background background={event.background} />
      <div className="event-welcome-container">
        {event.welcomeImageUrl && (
          <img
            src={event.welcomeImageUrl}
            className="welcome-image"
            alt="Welcome image for the event"
          />
        )}
        {event.iconUrl && (
          <img
            src={event.iconUrl}
            className="welcome-icon"
            alt="Icon for the event"
          />
        )}
        <h1 className="welcome-message">{event.name}</h1>
        {errors.length > 0 && (
          <div className="messages error">
            <ul>
              {errors.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="intro">Please select your event</p>
        <form onSubmit={go}>
          <select value={showing} onChange={(e) => setShowing(e.target.value)}>
            {event.showings.map((p) => (
              <option key={p.reference} value={p.reference}>
                {getShowingName(p)}
              </option>
            ))}
          </select>
          <Button className="primary" isSubmit={true} isLoading={false}>
            Go <ArrowIcon />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Event;
